import React, { useState, useEffect, useMemo } from "react";
import dynamic from "@redux-dynostore/react-redux";
import subspaced from "@redux-dynostore/react-redux-subspace";
import { attachReducer } from "@redux-dynostore/redux-subspace";
import { connect } from "react-redux";
import CustomModal, {
  ModalSize,
} from "../../ui-library/CustomModal/CustomModal";
import moment from "moment";
import {
  mapDispatchToProps,
  mapStateToProps,
  dietDiaryMenuItemsReducer,
} from "./DietDiaryMenuItems.store";
import {
  DietDairyTypes,
  dietDairyTypeToString,
} from "../DietDiary/DietDiary.constants";
import { useTranslation } from "react-i18next";
import FavoriteMeals from "../FavoriteMeals/FavoriteMeals";
import TopPageButtons from "../../library/TopPageButtons/TopPageButtons";
import DietDiary from "../DietDiary/DietDiary";

export const DietDiaryMenuItems = (props) => {
  const {
    dietMenuItems,
    refreshData,
    fetchDietDiaryMenuItems,
    participantDateOfEnrollment,
    resetState,
  } = props;

  const { t } = useTranslation();

  const [selectedDietDiaryOption, setSelectedDietDiaryOption] = useState(null);
  const [dietDiaryModalTitle, setDietDiaryModalTitle] = useState("");

  const minDateMeal = useMemo(() => {
    if (
      participantDateOfEnrollment &&
      moment(participantDateOfEnrollment) < moment().subtract(1, "days")
    ) {
      return moment().subtract(1, "days").toDate();
    }

    return moment(participantDateOfEnrollment).toDate();
  }, [participantDateOfEnrollment]);

  const dietDiaryOptions = useMemo(() => {
    const items =
      dietMenuItems?.map((item, i) => ({
        id: item.menuDisplayMealItemId || item.dietDiaryType,
        order: i,
        defaultItems: JSON.parse(item.defaultItemsJson),
        title: `${t("appText.add")} ${dietDairyTypeToString(
          item.dietDiaryType
        )}`,
        text: dietDairyTypeToString(item.dietDiaryType),
        type: item.dietDiaryType,
      })) || [];

    items.unshift(
      {
        id: "1",
        order: -2,
        text: t("appText.favorites"),
        type: DietDairyTypes.FAVORITES,
      },
      {
        id: "1a",
        order: -1,
        text: dietDairyTypeToString(DietDairyTypes.FOOD_DRINK),
        type: DietDairyTypes.FOOD_DRINK,
      }
    );

    return {
      ...items?.map((item) => ({
        ...item,
        dataTestId: `option-${item.type}`,
        onClick: () => {
          selectDietDiaryOption(item);
        },
      })),
    };
  }, [dietMenuItems, t]);

  useEffect(() => {
    resetState();
  }, []);

  useEffect(() => {
    fetchDietDiaryMenuItems();
  }, [fetchDietDiaryMenuItems]);

  const onSelectFavoriteMeal = (favoriteMeal) => {
    const type =
      JSON.parse(favoriteMeal.json)?.type ?? DietDairyTypes.FOOD_DRINK;

    setDietDiaryModalTitle(
      `${t("appText.add")} ${dietDairyTypeToString(type, t)}`
    );

    setSelectedDietDiaryOption({
      jsonData: favoriteMeal.json,
      type: type,
      favoriteId: favoriteMeal.favoriteId,
    });
  };

  const selectDietDiaryOption = (option) => {
    setDietDiaryModalTitle(`${t("appText.add")} ${option.text}`);
    setSelectedDietDiaryOption(option);
  };

  const onCloseDietDiaryModal = () => {
    setSelectedDietDiaryOption(null);
    setDietDiaryModalTitle(null);

    refreshData();
  };

  return (
    <>
      <CustomModal
        show={selectedDietDiaryOption !== null}
        title={dietDiaryModalTitle ?? ""}
        size={ModalSize.large}
        onClose={() => onCloseDietDiaryModal()}
      >
        {selectedDietDiaryOption?.type === DietDairyTypes.FAVORITES ? (
          <FavoriteMeals onSelectFavoriteMeal={onSelectFavoriteMeal} />
        ) : (
          <DietDiary
            defaultItems={selectedDietDiaryOption?.defaultItems}
            type={selectedDietDiaryOption?.type}
            dietDiaryOption={selectedDietDiaryOption}
            onSubmit={() => onCloseDietDiaryModal()}
            maxDate={moment().toDate()}
            minDate={minDateMeal}
            setModalTitle={setDietDiaryModalTitle}
            mealJsonData={selectedDietDiaryOption?.jsonData}
            baseFavoriteMealId={selectedDietDiaryOption?.favoriteId}
          />
        )}
      </CustomModal>

      <TopPageButtons
        buttons={Object.values(dietDiaryOptions).sort(
          (a, b) => a.order < b.order
        )}
      />
    </>
  );
};

export default dynamic(
  "dietDiaryMenuItemsReducer",
  subspaced({
    // We map extra state because we might need to acess other stores for data, subspace is self contained.
    mapExtraState: (state) => ({
      loginReducer: state.loginReducer,
      smartPageHeaderReducer: state.smartPageHeaderReducer,
    }),
  }),
  attachReducer(dietDiaryMenuItemsReducer)
)(connect(mapStateToProps, mapDispatchToProps)(DietDiaryMenuItems));
